import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import useBreakpoints from '@hooks/useBreakpoints';
import useTheme from '@cvent/blocks/hooks/useTheme';
import ScrollViewWithBars from '@cvent/carina/components/ScrollViewWithBars/ScrollViewWithBars';
import { useTranslate } from 'nucleus-text';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { useRouter } from 'next/router';
import { CSSObject } from '@emotion/react';
import Icon from '@components/shared/Icon';
import { notificationPropType } from '../notificationPropType';
import NotificationsLoading from './NotificationsLoading';
import EmptyContainer from './EmptyContainer';
import NotificationFlyoutContent from './NotificationFlyoutContent';
import NotificationFlyoutHeader from './NotificationFlyoutHeader';

const useStyles = (isEmpty: boolean): Record<string, CSSObject> => {
  const { isMobile, heightWindow, widthWindow } = useBreakpoints();
  const { font, depth, size, block } = useTheme();
  const { xOffset, yOffset, blurRadius, spreadRadius, color } = depth.boxShadow.hard;
  const borderRadius = size.border.radius.fiveX;

  return useMemo(
    () => ({
      container: !isMobile
        ? {
            width: 496,
            height: heightWindow,
            maxHeight: isEmpty ? 294 : 572,
            overflow: 'hidden',
            borderRadius,
            boxShadow: `${xOffset} ${yOffset} ${blurRadius} ${spreadRadius} ${color}`,
            paddingBottom: 24,
            backgroundColor: block.base
          }
        : {
            width: widthWindow,
            height: heightWindow,
            overflow: 'hidden',
            paddingTop: 12,
            backgroundColor: block.base
          },
      headerActionItemContainer: {
        display: 'flex',
        alignItems: 'center'
      },
      headerActionItemText: {
        marginLeft: 12,
        fontSize: font.mono.size.s
      },
      settingsAndListContainer: {
        display: 'flex',
        flexDirection: 'row',
        height: '100%'
      },
      singleScreenContainer: { flex: 1 },
      content: {
        height: '100%',
        position: 'relative'
      },
      settingsHidden: { left: 0 },
      settingsVisible: {
        left: '-100%'
      }
    }),
    [
      heightWindow,
      widthWindow,
      font,
      isMobile,
      xOffset,
      yOffset,
      blurRadius,
      color,
      spreadRadius,
      borderRadius,
      block,
      isEmpty
    ]
  );
};

const getHeaderActions = ({ theme, styles, translate, testID, content }) => {
  const notificationIsReadFalse = content.some(
    notification => notification.isRead === undefined || notification.isRead === false
  );
  const tempContentArray = notificationIsReadFalse
    ? [
        {
          value: 'allRead',
          label: () => (
            <div css={styles.headerActionItemContainer} {...injectTestId(`${testID}-action-markAllAsRead`)}>
              <Icon name="check" size="md" color={theme.font.color.soft} aria-hidden />
              <div css={styles.headerActionItemText}>{translate('Notifications-Flyout-Header-Mark-All-Read')}</div>
            </div>
          )
        }
      ]
    : [];
  return [
    ...tempContentArray,
    {
      value: 'settings',
      label: () => (
        <div css={styles.headerActionItemContainer} {...injectTestId(`${testID}-action-settings`)}>
          <Icon name="settings" size="md" color={theme.font.color.soft} aria-hidden />
          <div css={styles.headerActionItemText}>{translate('Notifications-Flyout-Header-Settings')}</div>
        </div>
      )
    }
  ];
};

function LoadingNotifications(): JSX.Element {
  return (
    <>
      {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
        <NotificationsLoading key={num} testID={num.toString()} />
      ))}
    </>
  );
}

/**
 * Flyout component for notifications
 */
function NotificationFlyout({
  toggleOpen,
  testID,
  content,
  eventId,
  notificationsLoading,
  markAndPublishNotificationsAsRead,
  surveyUrl
}) {
  const theme = useTheme();
  const styles = useStyles(!content.length);
  const { translate } = useTranslate();

  const headerActions = useMemo(
    () => getHeaderActions({ theme, styles, translate, testID, content }),
    [theme, styles, translate, testID, content]
  );
  const [, updateState] = useState({});
  const router = useRouter();
  const handleMenu = action => {
    if (action.value === 'settings') {
      toggleOpen();
      // navigate to settings page and have the notification tab open by default.
      router.push({
        pathname: `/events/${eventId}/settings`,
        query: { tabId: 'notifications' }
      });
    } else if (action.value === 'allRead') {
      const notificationIds = content.reduce((acc, notification) => {
        if (!notification.isRead) {
          return [...acc, notification.id];
        }
        return acc;
      }, []);
      if (notificationIds.length > 0) {
        markAndPublishNotificationsAsRead(notificationIds);
      }
    }
  };

  const notificationsScrollContainerRef = useRef();
  const notificationIdsRef = useRef<Set<string>>(new Set());
  const addToNotificationIds = useCallback(id => notificationIdsRef.current.add(id), [notificationIdsRef]);

  useEffect(() => {
    const ref = notificationIdsRef.current;
    return () => {
      if (!ref.size) {
        return;
      }
      const notificationIdsList = Array.from(ref);
      if (notificationIdsList.length) {
        markAndPublishNotificationsAsRead(notificationIdsList);
      }
      ref.clear();
    };
  }, [markAndPublishNotificationsAsRead]);

  useEffect(() => {
    // Force component re-render when ref changes
    if (notificationsScrollContainerRef.current) {
      updateState({});
    }
  }, []);

  let notificationFlyoutContent: JSX.Element = null;
  if (notificationsLoading) {
    notificationFlyoutContent = (
      <div css={styles.content}>
        <LoadingNotifications />
      </div>
    );
  } else if (!content.length) {
    notificationFlyoutContent = <EmptyContainer testID="noNotifications" />;
  } else {
    notificationFlyoutContent = (
      <div css={styles.content}>
        <NotificationFlyoutContent
          content={content}
          testID={`${testID}-content`}
          onClose={toggleOpen}
          eventId={eventId}
          rootRef={notificationsScrollContainerRef}
          markAndPublishNotificationsAsRead={markAndPublishNotificationsAsRead}
          onNotificationVisible={addToNotificationIds}
          surveyUrl={surveyUrl}
        />
      </div>
    );
  }

  return (
    <div css={styles.container} {...injectTestId(testID)}>
      <div css={styles.settingsAndListContainer}>
        <ScrollViewWithBars
          forwardScrollViewRef={notificationsScrollContainerRef}
          id="notification-list-scroll"
          header={
            <NotificationFlyoutHeader
              onClose={toggleOpen}
              actions={headerActions}
              testID={`${testID}-header`}
              onSelectAction={action => handleMenu(action)}
            />
          }
        >
          {notificationFlyoutContent}
        </ScrollViewWithBars>
      </div>
    </div>
  );
}

NotificationFlyout.propTypes = {
  toggleOpen: PropTypes.func,
  testID: PropTypes.string,
  content: PropTypes.arrayOf(PropTypes.shape(notificationPropType)),
  eventId: PropTypes.string,
  notificationsLoading: PropTypes.bool,
  markAndPublishNotificationsAsRead: PropTypes.func,
  navHeight: PropTypes.number,
  navHeaderVisible: PropTypes.bool,
  surveyUrl: PropTypes.string
};

NotificationFlyout.defaultProps = {
  testID: 'notificationFlyout'
};

export default NotificationFlyout;
